import {
  COLOMBIA_DEPARTMENTS,
  getMunicipalitiesByDepartment,
} from '@atreus/core';
import { Button, CollapsibleSection } from '@atreus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  KeyRound,
  MapPin,
  Pencil,
  Power,
  Puzzle,
  Settings,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { apiDelete, apiGet, apiPatch, apiPost } from '../cloud.js';
import { PermissionsMatrix } from '../features/permissions/PermissionsMatrix.js';
import { TenantFiscalSection } from '../features/tenant/TenantFiscalSection.js';
import { TenantGeneralSection } from '../features/tenant/TenantGeneralSection.js';
import { TenantTaxRatesSection } from '../features/tenant/TenantTaxRatesSection.js';

interface TenantDetail {
  tenant: {
    id: string;
    slug: string;
    name: string;
    active: boolean;
    createdAt: number;
    department: string | null;
    municipality: string | null;
    legalName: string | null;
    nit: string | null;
    dv: string | null;
    regime: 'simplificado' | 'comun';
    responsibilityCodesJson: string;
    economicActivityCode: string | null;
    country: string;
    currency: string;
    timezone: string;
  };
  users: {
    id: string;
    email: string;
    fullName: string;
    role: 'admin' | 'manager' | 'cashier' | 'waiter';
    active: boolean;
    createdAt: number;
  }[];
  features: {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
  }[];
}

const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Super-admin',
  admin: 'Administrador',
  manager: 'Gerente',
  cashier: 'Cajero',
  waiter: 'Mesero',
};

export const TenantDetailPage = ({ id }: { id: string }) => {
  const qc = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const detail = useQuery({
    queryKey: ['admin', 'tenants', id],
    queryFn: () => apiGet<TenantDetail>(`/admin/tenants/${id}`),
  });

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      apiPatch<{ ok: true }>(`/admin/tenants/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants', id] }),
  });

  const toggleFeature = useMutation({
    mutationFn: (vars: { featureId: string; enabled: boolean }) =>
      apiPatch<{ ok: true }>(`/admin/tenants/${id}/features/${vars.featureId}`, {
        enabled: vars.enabled,
      }),
    onMutate: async (vars) => {
      // Optimistic update — el switch responde sin esperar la red.
      await qc.cancelQueries({ queryKey: ['admin', 'tenants', id] });
      const prev = qc.getQueryData<TenantDetail>(['admin', 'tenants', id]);
      qc.setQueryData<TenantDetail>(['admin', 'tenants', id], (old) =>
        old
          ? {
              ...old,
              features: old.features.map((f) =>
                f.id === vars.featureId ? { ...f, enabled: vars.enabled } : f,
              ),
            }
          : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['admin', 'tenants', id], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants', id] }),
  });

  if (detail.isLoading) {
    return <div className="p-8 text-fg-muted">Cargando…</div>;
  }
  if (detail.error || !detail.data) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <p className="text-danger">
          {(detail.error as Error)?.message ?? 'Tenant no encontrado'}
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/tenants">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </Button>
      </div>
    );
  }

  const { tenant, users, features } = detail.data;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-8">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/tenants">
            <ArrowLeft className="size-4" /> Tenants
          </Link>
        </Button>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">{tenant.name}</h1>
          <p className="mt-1 font-mono text-sm text-fg-muted">{tenant.slug}</p>
        </div>
        <Button
          variant={tenant.active ? 'outline' : 'default'}
          onClick={() => setActive.mutate(!tenant.active)}
          disabled={setActive.isPending}
        >
          <Power className="size-4" />
          {tenant.active ? 'Desactivar' : 'Reactivar'}
        </Button>
      </header>

      <TenantGeneralSection tenantId={id} tenant={tenant} />

      <LocationCard tenantId={id} tenant={tenant} />

      <TenantFiscalSection tenantId={id} tenant={tenant} />

      <TenantTaxRatesSection tenantId={id} />

      <CollapsibleSection
        title="Features opcionales"
        icon={Puzzle}
        persistKey={`tenant-features-${id}`}
      >
        {features.length === 0 ? (
          <p className="text-sm text-fg-muted">Sin features opcionales.</p>
        ) : (
          <ul className="space-y-3">
            {features.map((f) => (
              <li key={f.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{f.name}</div>
                  <p className="text-xs text-fg-muted">{f.description}</p>
                </div>
                <ToggleSwitch
                  checked={f.enabled}
                  onChange={(enabled) =>
                    toggleFeature.mutate({ featureId: f.id, enabled })
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </CollapsibleSection>

      <UsersSection tenantId={id} users={users} />

      <CollapsibleSection
        title="Permisos por rol"
        icon={ShieldCheck}
        persistKey={`tenant-permissions-${id}`}
      >
        <PermissionsMatrix tenantId={id} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Configuraciones"
        icon={Settings}
        persistKey={`tenant-config-${id}`}
      >
        <p className="text-sm text-fg-muted">
          Próximamente — parámetros por módulo (pedidos, caja, tickets,
          descuentos…).
        </p>
      </CollapsibleSection>
    </div>
  );
};

type UserRow = TenantDetail['users'][number];

const UsersSection = ({
  tenantId,
  users,
}: {
  tenantId: string;
  users: UserRow[];
}) => {
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [resettingPwd, setResettingPwd] = useState<UserRow | null>(null);
  return (
    <>
      <CollapsibleSection
        title={`Usuarios (${users.length})`}
        icon={UsersIcon}
        persistKey={`tenant-users-${tenantId}`}
        defaultOpen
      >
        <div className="mb-3 flex justify-end">
          <Button size="sm" onClick={() => setInviting(true)}>
            <UserPlus className="size-4" /> Agregar
          </Button>
        </div>
        <ul className="divide-y divide-border rounded-md border border-border">
          {users.map((u) => (
            <UserRowItem
              key={u.id}
              user={u}
              tenantId={tenantId}
              onEdit={() => setEditing(u)}
              onResetPassword={() => setResettingPwd(u)}
            />
          ))}
        </ul>
      </CollapsibleSection>

      {inviting ? (
        <InviteUserDialog tenantId={tenantId} onClose={() => setInviting(false)} />
      ) : null}
      {editing ? (
        <EditUserDialog
          tenantId={tenantId}
          user={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {resettingPwd ? (
        <ResetPasswordDialog
          tenantId={tenantId}
          user={resettingPwd}
          onClose={() => setResettingPwd(null)}
        />
      ) : null}
    </>
  );
};

const UserRowItem = ({
  user,
  tenantId,
  onEdit,
  onResetPassword,
}: {
  user: UserRow;
  tenantId: string;
  onEdit: () => void;
  onResetPassword: () => void;
}) => {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () =>
      apiDelete<{ ok: true }>(`/admin/tenants/${tenantId}/users/${user.id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId] }),
  });

  const confirmDelete = (): void => {
    if (!window.confirm(`¿Eliminar al usuario "${user.fullName}"?`)) return;
    del.mutate();
  };

  const isAdmin = user.role === 'admin';

  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">{user.fullName}</div>
        <div className="text-xs text-fg-muted truncate">{user.email}</div>
      </div>
      <span className="rounded bg-surface-muted px-2 py-0.5 text-xs font-semibold">
        {ROLE_LABEL[user.role] ?? user.role}
      </span>
      {!user.active ? (
        <span className="text-xs text-danger">inactivo</span>
      ) : null}
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit} title="Editar">
          <Pencil className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onResetPassword}
          title="Cambiar contraseña"
        >
          <KeyRound className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={confirmDelete}
          disabled={isAdmin || del.isPending}
          title={
            isAdmin
              ? 'El administrador no se puede eliminar. Cambia primero su rol.'
              : 'Eliminar'
          }
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      {del.error ? (
        <span className="ml-2 text-xs text-danger">
          {(del.error as Error).message}
        </span>
      ) : null}
    </li>
  );
};

const OTHER_MUNICIPALITY = '__OTHER__';

const LocationCard = ({
  tenantId,
  tenant,
}: {
  tenantId: string;
  tenant: TenantDetail['tenant'];
}) => {
  const qc = useQueryClient();
  const [department, setDepartment] = useState(tenant.department ?? '');
  const [municipality, setMunicipality] = useState(tenant.municipality ?? '');
  // El select de municipio guarda el valor elegido del dropdown, que puede
  // ser un nombre listado o el sentinela `__OTHER__`. El texto libre vive
  // en `customMunicipality` y solo se usa cuando el sentinela está activo.
  const initialOptions = getMunicipalitiesByDepartment(tenant.department ?? '');
  const initialListed =
    tenant.municipality && initialOptions.includes(tenant.municipality);
  const [municipalitySelect, setMunicipalitySelect] = useState<string>(
    initialListed ? tenant.municipality! : tenant.municipality ? OTHER_MUNICIPALITY : '',
  );
  const [customMunicipality, setCustomMunicipality] = useState(
    initialListed ? '' : tenant.municipality ?? '',
  );

  // Al refetch sincroniza todo.
  useEffect(() => {
    setDepartment(tenant.department ?? '');
    const options = getMunicipalitiesByDepartment(tenant.department ?? '');
    const listed = tenant.municipality && options.includes(tenant.municipality);
    setMunicipalitySelect(
      listed
        ? tenant.municipality!
        : tenant.municipality
          ? OTHER_MUNICIPALITY
          : '',
    );
    setCustomMunicipality(listed ? '' : tenant.municipality ?? '');
    setMunicipality(tenant.municipality ?? '');
  }, [tenant.department, tenant.municipality]);

  // Cuando cambia el departamento, reseteamos el municipio (probablemente
  // la lista de opciones es distinta).
  const onDepartmentChange = (next: string): void => {
    setDepartment(next);
    setMunicipalitySelect('');
    setCustomMunicipality('');
    setMunicipality('');
  };

  // Efecto que mantiene sincronizado `municipality` (lo que se envía al API)
  // con la selección actual.
  useEffect(() => {
    if (municipalitySelect === OTHER_MUNICIPALITY) {
      setMunicipality(customMunicipality.trim());
    } else {
      setMunicipality(municipalitySelect);
    }
  }, [municipalitySelect, customMunicipality]);

  const options = getMunicipalitiesByDepartment(department);
  const showCustomInput = municipalitySelect === OTHER_MUNICIPALITY;

  const save = useMutation({
    mutationFn: () =>
      apiPatch<{ ok: true }>(`/admin/tenants/${tenantId}`, {
        department: department || null,
        municipality: municipality.trim() || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId] }),
  });

  const dirty =
    (department || null) !== (tenant.department || null) ||
    (municipality.trim() || null) !== (tenant.municipality || null);

  return (
    <CollapsibleSection
      title="Ubicación"
      icon={MapPin}
      persistKey={`tenant-location-${tenantId}`}
    >
      <p className="mb-3 text-xs text-fg-muted">
        Departamento y municipio del punto de venta. El POS los usa como
        valor por defecto del campo "ciudad" al crear pedidos con domicilio.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="loc-department"
              className="mb-1 block text-sm font-semibold"
            >
              Departamento
            </label>
            <select
              id="loc-department"
              autoComplete="off"
              value={department}
              onChange={(e) => onDepartmentChange(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
            >
              <option value="">— Selecciona —</option>
              {COLOMBIA_DEPARTMENTS.map((d) => (
                <option key={d.code} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="loc-municipality"
              className="mb-1 block text-sm font-semibold"
            >
              Municipio / Ciudad
            </label>
            <select
              id="loc-municipality"
              autoComplete="off"
              value={municipalitySelect}
              onChange={(e) => setMunicipalitySelect(e.target.value)}
              disabled={!department}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base disabled:opacity-50"
            >
              <option value="">
                {department ? '— Selecciona —' : 'Elige departamento primero'}
              </option>
              {options.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              {department ? (
                <option value={OTHER_MUNICIPALITY}>
                  Otro municipio…
                </option>
              ) : null}
            </select>
            {showCustomInput ? (
              <input
                autoComplete="off"
                value={customMunicipality}
                onChange={(e) => setCustomMunicipality(e.target.value)}
                placeholder="Nombre del municipio"
                className="mt-2 w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
              />
            ) : null}
          </div>
        </div>

        {save.error ? (
          <p className="mt-2 text-sm text-danger">{(save.error as Error).message}</p>
        ) : null}

        <div className="mt-4 flex justify-end">
          <Button
            disabled={!dirty || save.isPending || (showCustomInput && !customMunicipality.trim())}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Guardando…' : 'Guardar ubicación'}
          </Button>
        </div>
    </CollapsibleSection>
  );
};

const ToggleSwitch = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={
      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ' +
      (checked ? 'bg-fg' : 'bg-border')
    }
  >
    <span
      className={
        'inline-block size-5 rounded-full bg-surface shadow transition-transform ' +
        (checked ? 'translate-x-5' : 'translate-x-0.5')
      }
    />
  </button>
);

// ---------- Edit / Reset password dialogs ----------

const EditUserDialog = ({
  tenantId,
  user,
  onClose,
}: {
  tenantId: string;
  user: UserRow;
  onClose: () => void;
}) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState(user.email);
  const [fullName, setFullName] = useState(user.fullName);
  const [role, setRole] = useState<UserRow['role']>(user.role);
  const [active, setActive] = useState(user.active);

  const save = useMutation({
    mutationFn: () =>
      apiPatch<{ ok: true }>(
        `/admin/tenants/${tenantId}/users/${user.id}`,
        { email, fullName, role, active },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-lg font-black tracking-tight">Editar usuario</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>
        <form
          className="space-y-3 p-5"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (!save.isPending) save.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-semibold">Email</label>
            <input
              type="email"
              autoComplete="off"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Nombre</label>
            <input
              autoComplete="off"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Rol</label>
            <select
              autoComplete="off"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRow['role'])}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            >
              <option value="admin">Administrador</option>
              <option value="manager">Gerente</option>
              <option value="cashier">Cajero</option>
              <option value="waiter">Mesero</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Usuario activo
          </label>

          {save.error ? (
            <p className="text-sm text-danger">{(save.error as Error).message}</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordDialog = ({
  tenantId,
  user,
  onClose,
}: {
  tenantId: string;
  user: UserRow;
  onClose: () => void;
}) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const reset = useMutation({
    mutationFn: () =>
      apiPost<{ ok: true }>(
        `/admin/tenants/${tenantId}/users/${user.id}/reset-password`,
        { password },
      ),
    onSuccess: onClose,
  });

  const mismatch = password !== '' && confirm !== '' && password !== confirm;
  const canSubmit =
    !reset.isPending && password.length >= 8 && password === confirm;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-lg font-black tracking-tight">Cambiar contraseña</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>
        <form
          className="space-y-3 p-5"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) reset.mutate();
          }}
        >
          <p className="text-sm text-fg-muted">
            Define una nueva contraseña para <strong>{user.fullName}</strong>{' '}
            ({user.email}). El usuario deberá iniciar sesión nuevamente.
          </p>
          <div>
            <label className="mb-1 block text-sm font-semibold">Nueva contraseña</label>
            <input
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Confirmar</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
            {mismatch ? (
              <p className="mt-1 text-xs text-danger">Las contraseñas no coinciden.</p>
            ) : null}
          </div>

          {reset.error ? (
            <p className="text-sm text-danger">{(reset.error as Error).message}</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {reset.isPending ? 'Guardando…' : 'Cambiar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InviteUserDialog = ({
  tenantId,
  onClose,
}: {
  tenantId: string;
  onClose: () => void;
}) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'cashier' | 'waiter'>(
    'cashier',
  );

  const invite = useMutation({
    mutationFn: () =>
      apiPost(`/admin/tenants/${tenantId}/users`, {
        email,
        password,
        fullName,
        role,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-lg font-black tracking-tight">Agregar usuario</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>
        <form
          className="space-y-3 p-5"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (invite.isPending) return;
            invite.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-semibold">Email</label>
            <input
              type="email"
              autoComplete="off"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Nombre</label>
            <input
              autoComplete="off"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">
              Contraseña inicial
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
              placeholder="Mínimo 8 caracteres"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">Rol</label>
            <select
              autoComplete="off"
              value={role}
              onChange={(e) =>
                setRole(e.target.value as 'admin' | 'manager' | 'cashier' | 'waiter')
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2"
            >
              <option value="admin">Administrador</option>
              <option value="manager">Gerente</option>
              <option value="cashier">Cajero</option>
              <option value="waiter">Mesero</option>
            </select>
          </div>

          {invite.error ? (
            <p className="text-sm text-danger">{(invite.error as Error).message}</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                invite.isPending || !email || !fullName || password.length < 8
              }
            >
              {invite.isPending ? 'Creando…' : 'Crear'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
